import { createLazyResource } from '@magicdoorinc/ui';
import { createSignal, onMount } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { rentPaymentNotificationsSettingsRepository } from '~/repositories/settings/rentPaymentNotificationsSettingsRepository';
import type {
  RentPaymentNotificationSettings,
  RentPaymentNotificationSettingsCompanyPortalTypedSettingsWithOverridesDocument,
} from '~/swagger/settings/Api';

export const [RentPaymentNotificationsSettingsProvider, useRentPaymentNotificationsSettings] = createMagicDoorContext(
  'RentPaymentNotificationsSettings',
  () => {
    const repo = new rentPaymentNotificationsSettingsRepository();
    const [isLoading, setIsLoading] = createSignal<boolean>(false);
    const [error, setError] = createSignal<Error | undefined>(undefined);

    const [allRentPaymentNotificationsSettings, allRentPaymentNotificationsSettingsActions] =
      createLazyResource<RentPaymentNotificationSettingsCompanyPortalTypedSettingsWithOverridesDocument>(() =>
        repo.getAllRentPaymentNotificationsSettings()
      );

    // change update delete text setting like chats
    const updateRentPaymentNotificationsSettingsByLevel = async (
      level: string,
      levelId: string,
      payload: RentPaymentNotificationSettings
    ): Promise<void> => {
      setIsLoading(true);
      try {
        await repo.updateRentPaymentNotificationsSettingsByLevel(level, levelId, payload);
        setTimeout(() => {
          allRentPaymentNotificationsSettingsActions.refetch();
        }, 1000);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    const deleteRentPaymentNotificationsSettingsByLevel = async (level: string, levelId: string): Promise<void> => {
      setIsLoading(true);
      try {
        await repo.deleteRentPaymentNotificationsSettingsByLevel(level, levelId);
        setTimeout(() => {
          allRentPaymentNotificationsSettingsActions.refetch();
        }, 1000);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    const updateRentPaymentNotificationsSettings = async (payload: RentPaymentNotificationSettings): Promise<void> => {
      setIsLoading(true);
      try {
        await repo.changeRentPaymentNotificationsSettings(payload);
        setTimeout(() => {
          allRentPaymentNotificationsSettingsActions.refetch();
        }, 1000);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    onMount(() => {
      allRentPaymentNotificationsSettingsActions.fetch();
    });

    return {
      isLoading,
      error,

      updateRentPaymentNotificationsSettings,
      allRentPaymentNotificationsSettings,
      updateRentPaymentNotificationsSettingsByLevel,
      deleteRentPaymentNotificationsSettingsByLevel,
    };
  }
);
